import React, { createContext, useContext, useState, useEffect } from 'react';
import {useLocation} from "react-router-dom";

// Regex used to match the path for firing navigation events
const lessonListPath = /^.*\/embeddable\/lessonsList$/
const lessonPath = /^.*\/embeddable\/catchupId\/1\/lesson\/(\d+)(\/(\d+))*$/

export const AppGlobalConfigContext = createContext({
    isEmbedMode:false,
    setEmbedMode: (b) => {},
    fireNavigationEvents: (path, pageName) => {},
    batch: null,
    setBatchData: (b) => {}
}); // Default to non-embeddable

export function AppGlobalConfigProvider({ children }) {
    const [isEmbedMode, setIsEmbedMode] = useState(false);
    const [batch, setBatch] = useState();

    const location = useLocation();

    useEffect(() => {

        if(location) {
            const path = location.pathname;
            const isInEmbedMode = path.includes('embeddable'); // Check for path
            setIsEmbedMode(isInEmbedMode);
        }
    }, [location]); // Re-run

    // ARTFRAN-13314: hide chatbot widget when in embeddable/governo mode.
    // The GTM flag (window.CATCHUP_EMBED_MODE) handles suppression at load time;
    // this effect catches any widget that was already injected before embed mode
    // was resolved by the router (e.g. direct navigation to an /embeddable/ path).
    useEffect(() => {
        if (!isEmbedMode) return;

        const hideChatbot = () => {
            // FreshChat (Freshworks)
            if (window.fcWidget) window.fcWidget.hide();
            // Zendesk Messenger
            if (window.zE) {
                try { window.zE('messenger', 'hide'); } catch (_) {}
                try { window.zE('webWidget', 'hide'); } catch (_) {}
            }
            // HubSpot
            if (window.HubSpotConversations) window.HubSpotConversations.widget.close();
            // Intercom
            if (window.Intercom) window.Intercom('hide');
            // Generic fallback: mark body so GTM/CSS can also suppress
            document.body.setAttribute('data-catchup-embed', 'true');
        };

        // Run now (widget may already be present) and after a short delay
        // in case the widget script is still loading asynchronously via GTM.
        hideChatbot();
        const timer = setTimeout(hideChatbot, 3000);
        return () => clearTimeout(timer);
    }, [isEmbedMode]);

    const setEmbedMode = (b) => {
        setIsEmbedMode(b);
    }

    const setBatchData = (b) => {
        setBatch(b);
    }

    const postMessageToParent = (message) => {
        window.parent.postMessage(message, "*");
    }

    const postPageChangeEvent = (page, pageName) => {
        const href = window.location.href;
        postMessageToParent({event: 'pageChange', page: page, currentPageTitle: pageName, href: href});
    }

    const fireNavigationEvents = (path, pageName) => {

        if(!isEmbedMode) return;

        if(lessonListPath.test(path)) {
            postPageChangeEvent('lessonList', pageName);

        }else if(lessonPath.test(path)) {
            postPageChangeEvent('lesson', pageName);
        }
    }

    const value = {
        isEmbedMode,
        setEmbedMode,
        batch,
        setBatchData,
        fireNavigationEvents
    }

    return (
        <AppGlobalConfigContext.Provider value={value}>
            {children}
        </AppGlobalConfigContext.Provider>
    );
}

export function useAppGlobalConfig() {
    const context = useContext(AppGlobalConfigContext);
    if (!context) {
        throw new Error('useAppGlobalConfig must be used within an AppGlobalConfigProvider');
    }
    return context;
}